/**
 * componente reutilizable para filtrar y exportar datos de una tabla en excel
 * @param {Object} props
 * @param {string} props.column - Columna seleccionada para filtrar
 * @param {string} props.value - Valor a filtrar
 * @param {Array<{ value: string, label: string }>} props.columnOptions - Opciones de columnas disponibles para filtrar
 * @param {boolean} props.isFilterActive - Indica si el filtro está activo
 * @param {function} props.onColumnChange - Función que se ejecuta al cambiar la columna seleccionada
 * @param {function} props.onValueChange - Función que se ejecuta al cambiar el valor a filtrar 
 * @param {function} props.onApply - Función que se ejecuta al aplicar el filtro
 * @param {function} props.onClear - Función que se ejecuta al desactivar el filtro
 * @param {function} props.onExport - Función que se ejecuta al exportar los datos a Excel
 */

import React from "react";
import { FileSpreadsheet, Filter, FilterX } from "lucide-react";

const FilterActions = ({
    column,
    value,
    columnOptions = [],
    isFilterActive,
    onColumnChange,
    onValueChange,
    onApply,
    onClear,
    onExport,
}) => {
    return (
        <div className="rounded-lg border border-slate-300 bg-white p-4 shadow-sm lg:rounded-lg lg:border-gray-300 lg:shadow-none">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4 md:items-end">
                <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-slate-700 lg:text-gray-600">
                        Columna
                    </label>
                    <select
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200 text-slate-700 bg-white text-sm lg:border-gray-300 lg:rounded lg:focus:ring-blue-600 lg:text-gray-900"
                        value={column}
                        onChange={(event) => onColumnChange(event.target.value)}
                    >
                        <option value="">Seleccione una columna</option>
                        {columnOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-slate-700 lg:text-gray-600">
                        Valor a filtrar
                    </label>
                    <input
                        type="text"
                        placeholder="Buscar..."
                        value={value}
                        onChange={(event) => onValueChange(event.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white text-sm lg:border-gray-300 lg:rounded lg:text-gray-900 lg:placeholder:text-gray-400 lg:focus:ring-blue-600"
                    />
                </div>

                <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3 md:pl-4">
                    {!isFilterActive ? (
                        <button
                            type="button"
                            title="Aplicar filtro"
                            disabled={!column || !value.trim()}
                            className="rounded-lg px-4 py-2 border border-slate-300 bg-white text-slate-700 text-sm font-medium transition hover:bg-slate-50 lg:rounded lg:border-gray-300 lg:text-gray-700 lg:hover:bg-gray-50"
                            onClick={onApply}
                        >
                            <Filter size={20} color="#2563EB" strokeWidth={2} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            title="Desactivar filtro"
                            className="rounded-lg px-4 py-2 border border-slate-300 bg-white text-slate-700 text-sm font-medium transition hover:bg-slate-50 lg:rounded lg:border-gray-300 lg:text-gray-700 lg:hover:bg-gray-50"
                            onClick={onClear}
                        >
                            <FilterX size={20} color="#2563EB" strokeWidth={2} />
                        </button>
                    )}

                    <button
                        type="button"
                        title="Exportar a Excel"
                        className="rounded-lg px-4 py-2 border border-slate-300 bg-blue-600 text-white text-sm font-medium transition hover:bg-blue-500 lg:rounded lg:border-blue-300 lg:hover:bg-blue-500"
                        onClick={onExport}
                    >
                        <FileSpreadsheet size={20} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FilterActions;
